import React, { useRef, useState } from "react";

import { DatePickerProps as AntDatePickerProps, DatePicker as AntdDatePicker } from "antd";
import { RuleObject } from "antd/es/form";
import moment, { Moment } from "moment";
// eslint-disable-next-line import/no-extraneous-dependencies
import momentGenerateConfig from "rc-picker/lib/generate/moment";

import { generateRandomId, getLocalizedDateFormat, getMiddayMomentDate, parseInputRules } from "utils";

import { Form, Icon, InputLabel } from "components";

// Wrappers are needed, so all pickers consistently use Moment in the app and tests,
// avoiding fallback dates (like 2070) and type errors in AntD v5.
// See docs: https://ant.design/docs/react/use-custom-date-library
const AntPicker = AntdDatePicker.generatePicker<Moment>(momentGenerateConfig);

type DatePickerProps = Pick<AntDatePickerProps, "format" | "status" | "onBlur" | "picker"> & {
  showNow?: boolean;
  showTime?: boolean | { format: string };
  initialValue: moment.Moment | null;
  dataTestId?: string;
  className?: string;
  name?: string | string[];
  label?: string;
  labelClassName?: string;
  message?: string;
  rules?: RuleObject[];
  disabled?: boolean;
  onChange?: (value: moment.Moment, dateString: string) => void;
  defaultValue?: moment.Moment | null;
  defaultPickerValue?: moment.Moment | null;
  value?: moment.Moment | null;
  disabledDate?: (value: moment.Moment) => boolean;
};

type InputRef = {
  nativeElement: HTMLInputElement;
  focus: () => void;
  blur: () => void;
};

export const DatePicker = ({
  className = "",
  dataTestId,
  defaultValue,
  defaultPickerValue,
  disabled,
  disabledDate,
  format,
  initialValue,
  label,
  labelClassName = "",
  message,
  name,
  rules,
  showNow,
  showTime,
  status,
  value,
  picker,
  onBlur = () => {},
  onChange = () => {},
}: DatePickerProps) => {
  const inputElement = useRef<InputRef>(null);
  const [hasBeenFocused, setHasBeenFocused] = useState(false);
  const [internalValue, setInternalValue] = useState(initialValue);

  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    onBlur?.(e, {});
  };

  const handleChange = (value: moment.Moment, dateString: string | string[]) => {
    setInternalValue(value);
    onChange(value, Array.isArray(dateString) ? dateString[0] : dateString);
    setHasBeenFocused(false);
  };

  const handleLabelClick = () => {
    setHasBeenFocused(true);
    inputElement.current?.focus();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setHasBeenFocused(false);
    }
  };

  const active = !!(hasBeenFocused || internalValue || value);
  const required = rules && rules[0] && rules[0].required;

  const keyRef = useRef<string>(generateRandomId());
  const key = keyRef.current;

  return (
    <Form.Item
      className={`floating-input ${hasBeenFocused ? "active" : ""} ${disabled ? "disabled" : "message"} ${className}`}
      colon={false}
      label={
        <InputLabel
          active={active}
          required={required}
          label={label}
          className={labelClassName}
          dataTestId={dataTestId}
          handleClick={handleLabelClick}
          id={`${key}-label`}
        />
      }
      name={name}
      normalize={getMiddayMomentDate}
      rules={parseInputRules(rules, message)}
      initialValue={initialValue}
    >
      <AntPicker
        allowClear={false}
        defaultValue={defaultValue}
        defaultPickerValue={defaultPickerValue ?? moment()}
        disabled={disabled}
        disabledDate={disabledDate}
        format={getLocalizedDateFormat() ?? format}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={() => setHasBeenFocused(true)}
        onOpenChange={handleOpenChange}
        open={hasBeenFocused}
        placeholder={initialValue?.toString() || ""}
        placement="bottomRight"
        ref={inputElement}
        showNow={showNow}
        showTime={showTime}
        status={status}
        suffixIcon={<Icon iconName="tabler/calendar" />}
        value={value}
        data-testid={`${dataTestId}-picker`}
        id={key}
        key={key}
        aria-labelledby={`${key}-label`}
        picker={picker}
      />
    </Form.Item>
  );
};
